import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Plus, Search, Edit2, ToggleLeft, ToggleRight, Shield } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Label } from "shared/ui/label"
import {
  RoleDto,
  RoleUpsertRequest,
  PagedResultDto,
  searchRoles,
  createRole,
  updateRole,
  setRoleActive,
} from "modules/admin/api/roleService"

interface RoleMasterProps {
  currentUser?: { name?: string }
}

type FormState = {
  code: string
  displayName: string
  description: string
  isActive: boolean
}

const initialForm: FormState = {
  code: "",
  displayName: "",
  description: "",
  isActive: true,
}

export function RoleMaster({ currentUser }: RoleMasterProps) {
  const [items, setItems] = useState<RoleDto[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editing, setEditing] = useState<RoleDto | null>(null)
  const [form, setForm] = useState<FormState>(initialForm)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])

  const loadData = useCallback(
    async (currentPage = 1) => {
      setIsLoading(true)
      try {
        const result: PagedResultDto<RoleDto> = await searchRoles({
          search: searchTerm || undefined,
          isActive: statusFilter === "all" ? undefined : statusFilter === "active",
          page: currentPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? currentPage)
      } catch (err) {
        console.error(err)
        toast.error("โหลดข้อมูล Role ไม่สำเร็จ")
      } finally {
        setIsLoading(false)
      }
    },
    [searchTerm, statusFilter, pageSize]
  )

  useEffect(() => {
    loadData(1)
  }, [loadData])

  const resetForm = () => {
    setForm(initialForm)
    setEditing(null)
  }

  const openCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const openEdit = (item: RoleDto) => {
    setEditing(item)
    setForm({
      code: item.code,
      displayName: item.displayName,
      description: item.description ?? "",
      isActive: item.isActive,
    })
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const payload: RoleUpsertRequest = {
        code: form.code.trim(),
        displayName: form.displayName.trim(),
        description: form.description.trim() || null,
        isActive: form.isActive,
      }

      if (editing) {
        await updateRole(editing.roleId, payload)
        toast.success("อัปเดต Role สำเร็จ")
      } else {
        await createRole(payload)
        toast.success("สร้าง Role สำเร็จ")
      }
      setDialogOpen(false)
      resetForm()
      loadData(page)
    } catch (err: any) {
      console.error(err)
      const msg = err?.response?.data ?? "บันทึก Role ไม่สำเร็จ"
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggle = async (item: RoleDto) => {
    try {
      await setRoleActive(item.roleId, !item.isActive)
      toast.success("อัปเดตสถานะสำเร็จ")
      loadData(page)
    } catch (err: any) {
      const msg = err?.response?.data ?? "อัปเดตสถานะไม่สำเร็จ"
      toast.error(msg)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Role Master</h1>
          <p className="text-gray-600 text-sm">สร้างและจัดการ Role ของระบบ</p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> เพิ่ม Role
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>รายการ Role</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                className="pl-9"
                placeholder="ค้นหา code หรือชื่อ Role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadData(1)}
              />
            </div>
            <select
              className="border rounded-md px-3 py-2 text-sm text-gray-700"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">ทั้งหมด</option>
              <option value="active">ใช้งาน</option>
              <option value="inactive">ไม่ใช้งาน</option>
            </select>
            <Button variant="outline" onClick={() => loadData(1)} disabled={isLoading}>
              ค้นหา
            </Button>
          </div>

          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>ชื่อ Role</TableHead>
                  <TableHead>System</TableHead>
                  <TableHead>สถานะ</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.roleId}>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.isSystem && <Shield className="h-4 w-4 text-amber-600" />}
                        <span>{item.displayName}</span>
                      </div>
                      {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                    </TableCell>
                    <TableCell>{item.isSystem ? "System" : "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} disabled={item.isSystem} className="gap-1">
                        <Edit2 className="h-4 w-4" /> แก้ไข
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(item)}
                        disabled={item.isSystem}
                        className="gap-1"
                      >
                        {item.isActive ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-500" />}
                        {item.isActive ? "ปิด" : "เปิด"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                      {isLoading ? "กำลังโหลด..." : "ไม่พบข้อมูล"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>แสดง {items.length} จาก {totalItems} รายการ</span>
            <div className="space-x-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => loadData(page - 1)}>ก่อนหน้า</Button>
              <span>หน้า {page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => loadData(page + 1)}>ถัดไป</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "แก้ไข Role" : "เพิ่ม Role ใหม่"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                disabled={editing?.isSystem}
              />
            </div>
            <div>
              <Label>ชื่อ Role</Label>
              <Input
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                disabled={editing?.isSystem}
              />
            </div>
            <div>
              <Label>รายละเอียด</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                disabled={editing?.isSystem}
              />
              <Label htmlFor="isActive">ใช้งาน</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>ยกเลิก</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? "กำลังบันทึก..." : "บันทึก"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RoleMaster
